import * as React from 'react';

export interface AwsServiceIconProps {
  size?: number;
}

const COMPUTE = '#ED7100';
const COMPUTE_DARK = '#C25500';
const DATABASE = '#3B48CC';
const DATABASE_DARK = '#272A8C';
const MANAGEMENT = '#E7157B';
const MANAGEMENT_DARK = '#A40D55';

const Frame: React.FC<{
  bg: string;
  bgDark: string;
  size: number;
  children: React.ReactNode;
  title: string;
}> = ({ bg, bgDark, size, children, title }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    role="img"
    aria-label={title}
    focusable={false}
  >
    <title>{title}</title>
    <defs>
      <linearGradient id={`aws-grad-${title}`} x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={bg} />
        <stop offset="100%" stopColor={bgDark} />
      </linearGradient>
    </defs>
    <rect width="24" height="24" rx="3" fill={`url(#aws-grad-${title})`} />
    {children}
  </svg>
);

export const AwsAsgIcon: React.FC<AwsServiceIconProps> = ({ size = 22 }) => (
  <Frame bg={COMPUTE} bgDark={COMPUTE_DARK} size={size} title="Auto Scaling">
    <path
      fill="#fff"
      d="M12 4l3.2 3.6h-2v3.2h-2.4V7.6h-2L12 4zm0 16l-3.2-3.6h2v-3.2h2.4v3.2h2L12 20z"
    />
    <rect x="5.5" y="11" width="13" height="2" fill="#fff" opacity="0.7" />
  </Frame>
);

export const AwsEc2Icon: React.FC<AwsServiceIconProps> = ({ size = 22 }) => (
  <Frame bg={COMPUTE} bgDark={COMPUTE_DARK} size={size} title="EC2">
    <rect x="6" y="6" width="12" height="12" rx="1" fill="#fff" />
    <rect x="8" y="8" width="8" height="8" fill={COMPUTE} />
    <rect x="9.5" y="9.5" width="5" height="5" fill="#fff" />
    {[7, 11, 15].map((y) => (
      <React.Fragment key={y}>
        <rect x="4.5" y={y} width="1.5" height="1" fill="#fff" />
        <rect x="18" y={y} width="1.5" height="1" fill="#fff" />
      </React.Fragment>
    ))}
    {[7, 11, 15].map((x) => (
      <React.Fragment key={`v${x}`}>
        <rect x={x} y="4.5" width="1" height="1.5" fill="#fff" />
        <rect x={x} y="18" width="1" height="1.5" fill="#fff" />
      </React.Fragment>
    ))}
  </Frame>
);

export const AwsRdsIcon: React.FC<AwsServiceIconProps> = ({ size = 22 }) => (
  <Frame bg={DATABASE} bgDark={DATABASE_DARK} size={size} title="RDS">
    <ellipse cx="12" cy="7" rx="5.5" ry="1.8" fill="#fff" />
    <path
      fill="#fff"
      d="M6.5 7v10c0 1 2.46 1.8 5.5 1.8s5.5-.8 5.5-1.8V7c0 1-2.46 1.8-5.5 1.8S6.5 8 6.5 7z"
    />
    <path
      fill={DATABASE}
      opacity="0.25"
      d="M6.5 10.5c0 1 2.46 1.8 5.5 1.8s5.5-.8 5.5-1.8m-11 3.5c0 1 2.46 1.8 5.5 1.8s5.5-.8 5.5-1.8"
      stroke={DATABASE}
      strokeWidth="0.6"
    />
  </Frame>
);

export const AwsInstallerIcon: React.FC<AwsServiceIconProps> = ({ size = 22 }) => (
  <Frame bg={MANAGEMENT} bgDark={MANAGEMENT_DARK} size={size} title="Installer">
    <path fill="#fff" d="M12 4l7 3.5-7 3.5-7-3.5L12 4z" />
    <path fill="#fff" opacity="0.7" d="M5 11l7 3.5L19 11v2L12 16.5 5 13z" />
    <path fill="#fff" opacity="0.45" d="M5 15l7 3.5L19 15v2L12 20.5 5 17z" />
  </Frame>
);

export const AwsRegionIcon: React.FC<AwsServiceIconProps> = ({ size = 18 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    role="img"
    aria-label="AWS Region"
    focusable={false}
  >
    <title>AWS Region</title>
    <circle cx="12" cy="12" r="9" fill="none" stroke="#232F3E" strokeWidth="1.8" />
    <path
      d="M3 12h18M12 3a13 13 0 010 18M12 3a13 13 0 000 18"
      fill="none"
      stroke="#232F3E"
      strokeWidth="1.2"
    />
  </svg>
);
